import { useSession } from 'next-auth/react';
import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryProfileNoLoyaltyWallData =
  Content.Components['ProfileNoLoyaltyWallModuleDto'];

interface UseQueryProfileNoLoyaltyWallDataOptions {
  enabled?: boolean;
  initialData?: UseQueryProfileNoLoyaltyWallData;
  parameters: {
    id: string;
    pageId: string;
  };
}

const useQueryProfileNoLoyaltyWall = ({
  enabled = true,
  initialData,
  parameters,
}: UseQueryProfileNoLoyaltyWallDataOptions) => {
  const locale = getLocale();
  const { data: session } = useSession();

  const query = useQuery<UseQueryProfileNoLoyaltyWallData>(
    [QueryId.PROFILE_NO_LOYALTY_WALL, parameters, locale],
    async () => {
      const baseUrl = '/api/content-modules/profile-no-loyalty-wall';
      const queryParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${queryParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
            ...(session?.user.accessToken
              ? { ['Authorization']: `Bearer ${session?.user.accessToken}` }
              : {}),
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryProfileNoLoyaltyWall', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!parameters && enabled,
      initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryProfileNoLoyaltyWall;
